import React, { FC } from "react";
import styled from "styled-components";
import { lightTheme } from "../../styles/theme";
import { useTranslation } from "../../lang/TranslationContext";
import { openInNewTab } from "../../utils/openInNewTab";

type PlanBlockProps = {
  montlyPrice?: string;
  dailyPrice?: {
    dollars: number;
    cents: number;
  };
  countOfAssets: number;
  isFree?: boolean;
  isPopular?: boolean;
  isBestValue?: boolean;
};

export const PlanBlock: FC<PlanBlockProps> = ({
  dailyPrice,
  montlyPrice,
  countOfAssets,
  isFree = false,
  isPopular = false,
  isBestValue = false,
}) => {
  const { translations } = useTranslation();

  const getPlanFeatures = () => {
    if (countOfAssets === 1) return translations.landing.plans.pro1;
    if (countOfAssets === 5) return translations.landing.plans.pro5;
    if (countOfAssets === 14) return translations.landing.plans.pro14;
    return translations.landing.plans.pro1;
  };

  const planFeatures = !isFree ? getPlanFeatures() : null;

  return (
    <Wrap isFree={isFree} isPopular={isPopular} isBestValue={isBestValue}>
      <Top>
        <p>
          {isFree ? translations.landing.plans.free.heading : countOfAssets}{" "}
          {!isFree && translations.smallSubscriptionBlock.asset(countOfAssets)}
        </p>
      </Top>
      <Middle>
        {isFree ? (
          <>
            <FreePrice>
              <div>$0</div>
              <div>
                <p>/{translations.landing.plans.free.forever}</p>
              </div>
            </FreePrice>
            <FeatureList>
              <li>{translations.landing.plans.free.li1}</li>
              <li>{translations.landing.plans.free.li2}</li>
              <li>{translations.landing.plans.free.li3}</li>
              <li>{translations.landing.plans.free.li4}</li>
            </FeatureList>
          </>
        ) : (
          <>
            <section>
              <DailyPrice>
                <div>${dailyPrice!.dollars}</div>
                <div>
                  <p>{dailyPrice!.cents}</p>
                  <p>/{translations.landing.plans.perDay}</p>
                </div>
              </DailyPrice>
              <MonthlyPrice>
                ${montlyPrice} {translations.landing.plans.monthly}
              </MonthlyPrice>
            </section>
            {planFeatures && (
              <FeatureList>
                <li>{planFeatures.li1}</li>
                <li>{planFeatures.li2}</li>
                <li>{planFeatures.li3}</li>
                <li>{planFeatures.li4}</li>
                <li>{planFeatures.li5}</li>
                {planFeatures.li6 && <li>{planFeatures.li6}</li>}
              </FeatureList>
            )}
          </>
        )}
      </Middle>
      <Bottom onClick={() => openInNewTab("https://marketecho.app/signup")}>
        <p>
          {isFree
            ? translations.landing.plans.free.start
            : translations.landing.plans.button(countOfAssets)}
        </p>
      </Bottom>
    </Wrap>
  );
};

const Wrap = styled.div<{
  isFree?: boolean;
  isPopular?: boolean;
  isBestValue?: boolean;
}>`
  width: 332px;
  padding: 24px;
  background: #fff;
  border-radius: 20px;
  border: ${({ isFree, isPopular, isBestValue }) =>
    isFree
      ? "2px solid #cfcfcf"
      : isPopular || isBestValue
      ? "2px solid #047857"
      : "2px solid #ffffff"};
  position: relative;

  ${({ isFree }) =>
    isFree &&
    `
		&::before {
			content: 'FREE FOREVER';
			position: absolute;
			top: -12px;
			left: 50%;
			transform: translateX(-50%);
			background: #cfcfcf;
			color: black;
			padding: 4px 12px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
		}
	`}

  ${({ isPopular }) =>
    isPopular &&
    `
		&::before {
			content: 'MOST POPULAR';
			position: absolute;
			top: -12px;
			left: 50%;
			transform: translateX(-50%);
			background: #047857;
			color: #ffffff;
			padding: 4px 12px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
		}
	`}

  ${({ isBestValue }) =>
    isBestValue &&
    `
		&::before {
			content: 'BEST VALUE';
			position: absolute;
			top: -12px;
			left: 50%;
			transform: translateX(-50%);
			background: #047857;
			color: #ffffff;
			padding: 4px 12px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 700;
		}
	`}
`;

const Top = styled.div`
  p {
    font-size: 18px;
    color: ${lightTheme.typography.primary};
    font-weight: 600;
  }
`;

const Middle = styled.div`
  margin-top: 16px;

  section {
    display: flex;
    align-items: flex-end;
    margin-bottom: 16px;
  }
  > p {
    margin-top: 12px;
    color: ${lightTheme.typography.secondary};
    font-weight: 300;
    font-size: 14px;
  }
`;

const DailyPrice = styled.div`
  display: flex;

  > div {
    &:nth-of-type(1) {
      font-weight: 700;
      color: ${lightTheme.typography.primary};
      font-size: 33px;
    }
    &:nth-of-type(2) {
      margin-left: 4px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-weight: 700;
      color: ${lightTheme.typography.primary};
      font-size: 14px;

      p {
        &:nth-of-type(2) {
          font-weight: 400;
        }
      }
    }
  }
`;

const FreePrice = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;

  > div {
    &:nth-of-type(1) {
      font-weight: 700;
      color: ${lightTheme.typography.primary};
      font-size: 48px;
    }
    &:nth-of-type(2) {
      margin-left: 8px;
      margin-bottom: 8px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-weight: 400;
      color: ${lightTheme.typography.secondary};
      font-size: 16px;
    }
  }
`;

const MonthlyPrice = styled.div`
  margin-left: 15px;
  color: ${lightTheme.typography.secondary};
  font-weight: 400;
  font-size: 16px;
`;

const Bottom = styled.div`
  margin-top: 16px;
  border-radius: 8px;
  width: 100%;
  background: ${lightTheme.typography.primary};
  padding: 8px 16px;
  transition: 0.3s;
  cursor: pointer;

  p {
    text-align: center;
    color: #fff;
    font-weight: 500;
    font-size: 18px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: ${lightTheme.typography.primary};
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 8px;
    padding-left: 24px;
    position: relative;

    &::before {
      content: "→";
      position: absolute;
      left: 0;
      color: #898989;
      font-weight: 700;
      font-size: 16px;
    }
  }
`;
